import React, { useContext, useState, useCallback } from 'react'

import { Box } from '@gousto-internal/citrus-react'
import { PortionSizeSelector } from 'PortionSizeSelector/PortionSizeSelector'
import PropTypes from 'prop-types'

import { portionSizesConfig } from 'config/portionSizes'
import { actionTypes } from 'routes/Account/Subscription/context/reducers'

import { SettingSection } from '../../../../components/SettingSection'
import { SubscriptionContext } from '../../../../context'
import {
  getNumPortions,
  getIsBoxLoaded,
  getSelectedBoxSize,
  getSubscriptionSettingsUnsupported,
} from '../../../../context/selectors/box'
import { getOpenOrders } from '../../../../context/selectors/orders'
import { getSubscriptionPortionOptions } from '../../../../context/selectors/subscriptionOptions'
import { useUpdateSubscription } from '../../../../hooks/useUpdateSubscription'
import { trackSubscriptionSettingsChange } from '../../../../tracking'
import { BoxSizeConfirmationModal } from './BoxSizeConfirmationModal'

export const BoxSize = ({ accessToken, isMobile }) => {
  const { state, dispatch } = useContext(SubscriptionContext)

  const isLoaded = getIsBoxLoaded(state)
  const currentBoxSize = getNumPortions(state)
  const chosenBoxSize = getSelectedBoxSize(state)
  const isUnsupported = getSubscriptionSettingsUnsupported(state)
  const boxSizesOptions = getSubscriptionPortionOptions(state)
  const [shouldSubmit, setShouldSubmit] = useState(false)

  const settingName = 'box_size'
  const [, updateResponse, updateError] = useUpdateSubscription({
    accessToken,
    trigger: {
      shouldRequest: shouldSubmit,
      setShouldRequest: setShouldSubmit,
    },
    data: {
      numPortions: chosenBoxSize,
    },
    settingName,
  })

  const didUpdateComplete = typeof updateResponse?.status === 'string'

  const onSubmit = () => {
    trackSubscriptionSettingsChange({ settingName, action: 'update' })()
    setShouldSubmit(true)
  }

  const handleChangeBoxSize = useCallback(
    (event) => {
      const newValue = Number(event.target.value)
      dispatch({
        type: actionTypes.UPDATE_SELECTED_BOX_SIZE,
        data: { numPortions: newValue },
      })
    },
    [dispatch],
  )

  const isCtaDisabled = chosenBoxSize === currentBoxSize || !chosenBoxSize

  return (
    <Box padding={0}>
      <SettingSection
        icon="portions"
        title="Box size"
        instruction="Edit box size"
        ctaText="Save"
        isCtaDisabled={isCtaDisabled}
        onChildrenRender={(collapseSection) => {
          if (!isUnsupported) {
            return
          }
          // a workaround to close the <ExpandableSection /> when external state changes
          collapseSection()
        }}
        renderCurrentValue={
          <p data-testing="current-box-size">{chosenBoxSize || currentBoxSize} people</p>
        }
        onSubmit={onSubmit}
        onEditClick={trackSubscriptionSettingsChange({ settingName, action: 'edit' })}
        isMobile={isMobile}
        testingSelector="box-size"
      >
        {isLoaded && (
          <PortionSizeSelector
            portionSize={Number(chosenBoxSize)}
            availablePortionSizes={boxSizesOptions}
            config={portionSizesConfig}
            onChange={handleChangeBoxSize}
          />
        )}
      </SettingSection>
      <BoxSizeConfirmationModal
        openOrders={getOpenOrders(state)}
        numPortions={currentBoxSize}
        didUpdateComplete={didUpdateComplete}
        didUpdateFail={updateError}
        updateResponse={updateResponse}
      />
    </Box>
  )
}

BoxSize.propTypes = {
  accessToken: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
}
